import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, User, FileText, Upload, CheckCircle, Shield, Trash2, Save, Camera, CreditCard, Landmark, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import NationalitySelect from "../components/NationalitySelect";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { type: "passport", label: "Passport", icon: FileText, desc: "Clear scan of passport bio page" },
  { type: "photo", label: "Passport Photo", icon: Camera, desc: "Recent passport-sized photo (white background)" },
  { type: "aadhaar", label: "Aadhaar Card", icon: CreditCard, desc: "Front and back of Aadhaar card" },
  { type: "pan", label: "PAN Card", icon: CreditCard, desc: "Clear photo of PAN card" },
  { type: "bank_statement", label: "Bank Statement", icon: Landmark, desc: "Last 6 months bank statements" },
  { type: "other_id", label: "Other ID Proof", icon: FileText, desc: "Any government-issued ID" },
];

export default function ProfilePage() {
  const { user, loading: authLoading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ name: "", phone: "", nationality: "", date_of_birth: "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
    if (user) {
      setProfile({ name: user.name || "", phone: user.phone || "", nationality: user.nationality || "", date_of_birth: user.date_of_birth || "" });
      fetchDocs();
    }
  }, [user, authLoading]);

  const fetchDocs = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${API}/saved-documents`,  { headers: { Authorization: `Bearer ${token}` } });
      setDocs(data);
    } catch {}
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/auth/profile`, profile,  { headers: { Authorization: `Bearer ${token}` } });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      checkAuth();
    } catch {} finally { setSaving(false); }
  };

  const handleUploadDoc = async (docType, file) => {
    setUploading(docType);
    try {
      const token = localStorage.getItem("token");
      const form = new FormData();
      form.append("document_type", docType);
      form.append("file", file);
      await axios.post(`${API}/saved-documents`, form, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      fetchDocs();
    } catch {} finally { setUploading(""); }
  };

  const uploadedTypes = docs.map(d => d.document_type);

  if (authLoading || !user) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen px-6 py-8 pt-24 bg-slate-50">
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-blue-600 mb-6"><ArrowLeft className="w-4 h-4" /> Dashboard</button>

        <h1 className="heading-font text-3xl font-bold text-slate-900 mb-8">My Profile</h1>

        {/* Security Badge */}
        <div className="security-badge rounded-xl p-3 flex items-center gap-2 mb-8">
          <Shield className="w-4 h-4 text-emerald-600" />
          <span className="text-xs font-medium text-emerald-700">Your data is encrypted & secure. Documents stored with bank-grade security.</span>
        </div>

        {/* Profile Info */}
        <div className="card-elevated rounded-2xl p-7 mb-8" data-testid="profile-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-lg font-bold">
              {user.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div>
              <h2 className="heading-font text-lg font-semibold text-slate-900">Personal Information</h2>
              <p className="text-xs text-slate-400">{user.email}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-sm text-slate-600 mb-1 block font-medium">Full Name</label>
              <Input value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                className="h-11 rounded-lg border-slate-200" />
            </div>
            <div>
              <label className="text-sm text-slate-600 mb-1 block font-medium">Phone</label>
              <Input value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} placeholder="+91 98765 43210"
                className="h-11 rounded-lg border-slate-200" />
            </div>
            <div>
              <label className="text-sm text-slate-600 mb-1 block font-medium">Nationality</label>
              <NationalitySelect value={profile.nationality} onValueChange={v => setProfile(p => ({ ...p, nationality: v }))} />
            </div>
            <div>
              <label className="text-sm text-slate-600 mb-1 block font-medium">Date of Birth</label>
              <Input type="date" value={profile.date_of_birth} onChange={e => setProfile(p => ({ ...p, date_of_birth: e.target.value }))}
                className="h-11 rounded-lg border-slate-200" />
            </div>
          </div>

          <button onClick={handleSaveProfile} disabled={saving} data-testid="save-profile"
            className="btn-premium text-white font-semibold px-6 py-2.5 rounded-lg flex items-center gap-2 text-sm disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <><CheckCircle className="w-4 h-4" /> Saved!</> : <><Save className="w-4 h-4" /> Save Profile</>}
          </button>
        </div>

        {/* Saved Documents */}
        <div className="card-elevated rounded-2xl p-7" data-testid="documents-section">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="heading-font text-lg font-semibold text-slate-900">Saved Documents</h2>
              <p className="text-xs text-slate-400 mt-0.5">Upload once, reuse for every application</p>
            </div>
            <span className="text-xs text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100 flex items-center gap-1">
              <Shield className="w-3 h-3" /> Encrypted
            </span>
          </div>

          <div className="space-y-3">
            {DOC_TYPES.map(dt => {
              const isUploaded = uploadedTypes.includes(dt.type);
              const isUploading = uploading === dt.type;
              const uploadedDoc = docs.find(d => d.document_type === dt.type);
              return (
                <div key={dt.type} className={`flex items-center justify-between p-4 rounded-xl border transition-all ${isUploaded ? "bg-emerald-50/50 border-emerald-200" : "bg-slate-50 border-slate-100 hover:border-blue-200"}`}
                  data-testid={`saved-doc-${dt.type}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isUploaded ? "bg-emerald-100" : "bg-white border border-slate-200"}`}>
                      {isUploaded ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <dt.icon className="w-5 h-5 text-slate-400" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{dt.label}</p>
                      <p className="text-xs text-slate-400">{isUploaded ? (uploadedDoc?.original_name || "Uploaded") : dt.desc}</p>
                    </div>
                  </div>
                  <label className="cursor-pointer">
                    <input type="file" className="hidden" accept="image/*,.pdf" onChange={e => e.target.files[0] && handleUploadDoc(dt.type, e.target.files[0])} />
                    <span className={`text-xs font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5 transition-all ${
                      isUploaded ? "text-emerald-600 border border-emerald-200 hover:bg-emerald-50" : "text-blue-600 border border-blue-200 hover:bg-blue-50"}`}>
                      {isUploading ? <><Loader2 className="w-3 h-3 animate-spin" /> Uploading...</> : isUploaded ? <><Upload className="w-3 h-3" /> Replace</> : <><Upload className="w-3 h-3" /> Upload</>}
                    </span>
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
